import React, {
  useEffect,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import PageDropdownSelect from "./PageDropdownSelect";
import type { IPageInfo } from "@/interface/common.interface";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

type PaginationItem =
  | { type: "page"; value: number }
  | { type: "dots"; key: string };

interface ITablePaginationProps {
  handleChangePageSize?: (value: number) => void;
  paginationData: IPageInfo;
  setPaginationData: Dispatch<SetStateAction<IPageInfo>>;
  isLoading?: boolean;
  itemLabel?: string;
  baseClass?: string;
}

const getPaginationItems = (
  currentPage: number,
  totalPages: number,
): PaginationItem[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => ({
      type: "page" as const,
      value: index + 1,
    }));
  }

  if (currentPage <= 3) {
    return [
      { type: "page", value: 1 },
      { type: "page", value: 2 },
      { type: "page", value: 3 },
      { type: "dots", key: "right-dots" },
      { type: "page", value: totalPages },
    ];
  }

  if (currentPage >= totalPages - 2) {
    return [
      { type: "page", value: 1 },
      { type: "dots", key: "left-dots" },
      { type: "page", value: totalPages - 2 },
      { type: "page", value: totalPages - 1 },
      { type: "page", value: totalPages },
    ];
  }

  return [
    { type: "page", value: 1 },
    { type: "dots", key: "left-dots" },
    { type: "page", value: currentPage - 1 },
    { type: "page", value: currentPage },
    { type: "page", value: currentPage + 1 },
    { type: "dots", key: "right-dots" },
    { type: "page", value: totalPages },
  ];
};

const TablePagination: React.FC<ITablePaginationProps> = ({
  handleChangePageSize,
  paginationData,
  setPaginationData,
  isLoading = false,
  itemLabel,
  baseClass = "flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed",
}) => {
  const { currentPage, pageSize, total, totalPages } = paginationData;
  const safeTotalPages = totalPages > 0 ? totalPages : 1;
  const safePage = Math.min(Math.max(currentPage, 1), safeTotalPages);
  const isFirstPage = safePage === 1;
  const isLastPage = safePage === safeTotalPages;

  useEffect(() => {
    if (currentPage === safePage) return;
    setPaginationData((prev) => ({ ...prev, currentPage: safePage }));
  }, [safePage, currentPage, setPaginationData]);

  const changePage = (nextPage: number) => {
    if (isLoading) return;
    if (nextPage < 1 || nextPage > safeTotalPages) return;
    setPaginationData((prev) => ({ ...prev, currentPage: nextPage }));
  };

  const changeLimit = (nextLimit: number) => {
    if (isLoading) return;
    if (handleChangePageSize) {
      handleChangePageSize(nextLimit);
      return;
    }
    setPaginationData((prev) => ({ ...prev, pageSize: nextLimit, currentPage: 1 }));
  };

  const paginationItems = useMemo(() => {
    return getPaginationItems(safePage, safeTotalPages);
  }, [safePage, safeTotalPages]);



  if (total <= 10) {
    return null;
  }

  return (
    <div className="mt-6 flex justify-between">
      <PageDropdownSelect
        limit={pageSize}
        onChangeLimit={changeLimit}
        page={safePage}
        total={total}
        itemLabel={itemLabel}
        disabled={isLoading}
      />

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={baseClass}
          onClick={() => changePage(1)}
          disabled={isFirstPage || isLoading}
          aria-label="First page"
        >
          <ChevronsLeft size={16} />
        </button>

        <button
          type="button"
          className={baseClass}
          onClick={() => changePage(safePage - 1)}
          disabled={isFirstPage || isLoading}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {paginationItems?.map((item, index) =>
          item?.type === "dots" ? (
            <span
              key={item?.key || `dots-${index}`}
              className="flex h-9 w-9 items-center justify-center text-sm font-semibold text-slate-400"
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              key={item?.value}
              type="button"
              onClick={() => changePage(item?.value)}
              disabled={isLoading}
              className={`${baseClass} ${item?.value === safePage
                ? "!bg-primary !text-white !border-primary"
                : ""
                }`}
              aria-current={item?.value === safePage ? "page" : undefined}
            >
              {item.value}
            </button>
          ),
        )}

        <button
          type="button"
          className={baseClass}
          onClick={() => changePage(safePage + 1)}
          disabled={isLastPage || isLoading}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>

        <button
          type="button"
          className={baseClass}
          onClick={() => changePage(safeTotalPages)}
          disabled={isLastPage || isLoading}
          aria-label="Last page"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default TablePagination;
